import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Building, Calendar, MapPin, Plus, X, Edit3, Trash2, Clock } from 'lucide-react';
import api from '../components/api';
import { toast } from 'react-toastify';
import useGlobale from '../components/global';
import Sidebar from '../components/SideBar';
import Header from '../components/Header';
import DemandeurMenu from '../components/DemandeurMenu';
import countryList from 'react-select-country-list';

const ExperiencePage = () => {
    const { user } = useGlobale();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [experiences, setExperiences] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedExperience, setSelectedExperience] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState(null);
    const [formData, setFormData] = useState({
        nom_entreprise: '',
        poste: '',
        date_debut: '',
        date_fin: '',
        description: '',
        pays: 'NE', // Niger ISO code par défaut
    });

    // Get country options from react-select-country-list
    const countryOptions = useMemo(() => countryList().getData(), []);

    // État pour suivre si le formulaire contient des erreurs
    const [errors, setErrors] = useState({});

    // Set sidebar initial state based on screen size
    useEffect(() => {
        const handleResize = () => {
            setIsSidebarOpen(window.innerWidth >= 1024);
        };
        
        // Set initial state
        handleResize();
        
        // Add event listener
        window.addEventListener('resize', handleResize);
        
        // Cleanup
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        fetchExperiences();
    }, []);

    const fetchExperiences = async () => {
        setLoading(true);
        try {
            const response = await api.get('demandeur/experiences/');
            setExperiences(response.data);
        } catch (error) {
            console.error('Erreur lors de la récupération des expériences:', error);
            toast.error('Erreur lors de la récupération de vos expériences professionnelles');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        
        // Effacer l'erreur lorsqu'un champ est modifié
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.nom_entreprise.trim()) {
            newErrors.nom_entreprise = "Le nom de l'entreprise est requis";
        }
        
        if (!formData.poste.trim()) {
            newErrors.poste = "Le poste occupé est requis";
        }
        
        if (!formData.date_debut) {
            newErrors.date_debut = "La date de début est requise";
        }
        
        if (!formData.description.trim()) {
            newErrors.description = "La description de l'expérience est requise";
        }
        
        if (!formData.pays) {
            newErrors.pays = "Le pays est requis";
        }
        
        // Si date_fin est spécifiée, vérifier qu'elle est après date_debut
        if (formData.date_fin && new Date(formData.date_fin) < new Date(formData.date_debut)) {
            newErrors.date_fin = "La date de fin doit être postérieure à la date de début";
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setLoading(true);
        try {
            let response;
            if (isEditMode && selectedExperience) {
                response = await api.put(`demandeur/experiences/${selectedExperience.id}/`, {
                    ...formData,
                    utilisateur: user.id,
                });
                toast.success('Expérience mise à jour avec succès');
            } else {
                response = await api.post('demandeur/experiences/', {
                    ...formData,
                    utilisateur: user.id,
                });
                toast.success('Expérience ajoutée avec succès');
            }
            
            // Mise à jour de la liste des expériences
            fetchExperiences();
            setShowModal(false);
        } catch (error) {
            console.error('Erreur lors de la sauvegarde de l\'expérience:', error);
            
            if (error.response && error.response.data) {
                if (typeof error.response.data === 'object') {
                    setErrors(error.response.data);
                } else {
                    toast.error('Erreur lors de la sauvegarde de l\'expérience');
                }
            } else {
                toast.error('Erreur de connexion au serveur');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        setLoading(true);
        try {
            await api.delete(`demandeur/experiences/${id}/`);
            toast.success('Expérience supprimée avec succès');
            setDeleteConfirmation(null);
            // Mettre à jour la liste des expériences
            fetchExperiences();
        } catch (error) {
            console.error('Erreur lors de la suppression de l\'expérience:', error);
            toast.error('Erreur lors de la suppression de l\'expérience');
        } finally {
            setLoading(false);
        }
    };

    const openEditModal = (experience) => {
        setIsEditMode(true);
        setSelectedExperience(experience);
        setFormData({
            nom_entreprise: experience.nom_entreprise,
            poste: experience.poste,
            date_debut: experience.date_debut ? experience.date_debut.split('T')[0] : '',
            date_fin: experience.date_fin ? experience.date_fin.split('T')[0] : '',
            description: experience.description,
            pays: experience.pays,
        });
        setShowModal(true);
    };

    const openAddModal = () => {
        setIsEditMode(false);
        setSelectedExperience(null);
        setFormData({
            nom_entreprise: '',
            poste: '',
            date_debut: '',
            date_fin: '',
            description: '',
            pays: 'NE', // Niger ISO code par défaut
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setErrors({});
    };

    // Fonction pour formater l'affichage des dates
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    };

    // Fonction pour calculer la durée de l'expérience
    const calculateDuration = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = endDate ? new Date(endDate) : new Date();
        
        const diffTime = Math.abs(end - start);
        const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30));
        
        const years = Math.floor(diffMonths / 12);
        const months = diffMonths % 12;
        
        if (years > 0) {
            return `${years} an${years > 1 ? 's' : ''} ${months > 0 ? `et ${months} mois` : ''}`;
        }
        return `${months} mois`;
    };

    // Fonction pour obtenir le nom complet du pays à partir du code ISO
    const getCountryNameFromCode = (code) => {
        const country = countryOptions.find(country => country.value === code);
        return country ? country.label : code;
    };

    const renderExperienceCards = () => {
        if (experiences.length === 0) {
            return (
                <div className="bg-white rounded-xl shadow-md p-8 text-center">
                    <div className="mb-6">
                        <div className="h-20 w-20 rounded-full bg-orange-100 flex items-center justify-center text-orange-500 mx-auto">
                            <Briefcase size={36} strokeWidth={1.5} />
                        </div>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Aucune expérience professionnelle</h3>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                        Ajoutez vos expériences professionnelles pour mettre en valeur votre parcours auprès des recruteurs.
                    </p>
                </div>
            );
        }

        return (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {experiences.map((experience) => (
                    <div 
                        key={experience.id} 
                        className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition duration-300 transform hover:-translate-y-1 border border-gray-100"
                    >
                        <div className="relative">
                            <div className="absolute top-0 right-0 pt-4 pr-4 flex space-x-1 z-10">
                                <button 
                                    onClick={() => openEditModal(experience)}
                                    className="bg-blue-50 hover:bg-blue-100 text-blue-600 p-2 rounded-full transition-colors duration-200"
                                    title="Modifier"
                                >
                                    <Edit3 size={16} />
                                </button>
                                <button 
                                    onClick={() => setDeleteConfirmation(experience.id)}
                                    className="bg-red-50 hover:bg-red-100 text-red-600 p-2 rounded-full transition-colors duration-200"
                                    title="Supprimer"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                            
                            <div className="bg-gradient-to-r from-orange-500 to-orange-600 h-3 w-full"></div>
                            
                            <div className="p-6">
                                <div className="flex items-start">
                                    <div className="bg-orange-100 p-3 rounded-full mr-4">
                                        <Briefcase className="h-7 w-7 text-orange-600" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-800 mb-1">{experience.poste}</h3>
                                        <p className="text-orange-600 font-medium flex items-center">
                                            <Building size={16} className="mr-2" />
                                            {experience.nom_entreprise}
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="mt-5 bg-gray-50 rounded-lg p-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="flex items-center">
                                            <div className="bg-blue-50 p-2 rounded-full mr-3">
                                                <Calendar size={16} className="text-blue-600" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 font-medium">PÉRIODE</p>
                                                <p className="text-gray-700 font-medium">
                                                    {formatDate(experience.date_debut)} - {experience.date_fin ? formatDate(experience.date_fin) : 'Présent'}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center">
                                            <div className="bg-green-50 p-2 rounded-full mr-3">
                                                <Clock size={16} className="text-green-600" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 font-medium">DURÉE</p>
                                                <p className="text-gray-700 font-medium">
                                                    {calculateDuration(experience.date_debut, experience.date_fin)}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center md:col-span-2">
                                            <div className="bg-purple-50 p-2 rounded-full mr-3">
                                                <MapPin size={16} className="text-purple-600" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 font-medium">LOCALISATION</p>
                                                <p className="text-gray-700 font-medium">
                                                    {getCountryNameFromCode(experience.pays)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="mt-5">
                                    <h4 className="text-sm font-semibold text-gray-500 uppercase mb-2 flex items-center">
                                        <div className="h-1 w-3 bg-orange-500 rounded mr-2"></div> Description
                                    </h4>
                                    <p className="text-gray-700 leading-relaxed">
                                        {experience.description}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderContent = () => {
        if (loading && experiences.length === 0) {
            return (
                <div className="flex justify-center items-center h-full">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-orange-500"></div>
                </div>
            );
        }

        return (
            <>
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
                    <div>
                        <h1 className="text-2xl font-bold text-[#437011] mb-2">
                            Expériences Professionnelles
                        </h1>
                        <p className="text-gray-600">Gérez votre parcours professionnel et mettez en valeur vos compétences</p>
                    </div>
                    <button
                        onClick={openAddModal}
                        className="bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-orange-700 transition mt-4 md:mt-0 w-full md:w-auto justify-center"
                    >
                        <Plus size={18} className="mr-2" />
                        Ajouter une expérience
                    </button>
                </div>

                {renderExperienceCards()}
            </>
        );
    };

    return (
        <div className="flex min-h-screen bg-[#F4F8E9]">
            <Sidebar
                isOpen={isSidebarOpen}
                menuItems={DemandeurMenu()}
                onClose={() => setIsSidebarOpen(false)}
            />

            <div className="flex-1 flex flex-col w-full">
                <Header
                    isSidebarOpen={isSidebarOpen}
                    setIsSidebarOpen={setIsSidebarOpen}
                    user={user}
                />
                <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 pb-20 lg:pb-8 w-full">
                    {renderContent()}
                </main>
            </div>

            {/* Modal pour ajouter/éditer une expérience */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-gray-800">
                                {isEditMode ? "Modifier l'expérience" : "Ajouter une nouvelle expérience"}
                            </h3>
                            <button
                                onClick={closeModal}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Poste occupé <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="poste"
                                        value={formData.poste}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.poste ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                        placeholder="Ex: Développeur Web, Chef de Projet, etc."
                                    />
                                    {errors.poste && <p className="mt-1 text-sm text-red-600">{errors.poste}</p>}
                                </div>
                                
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Nom de l'entreprise <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="nom_entreprise"
                                        value={formData.nom_entreprise}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.nom_entreprise ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                        placeholder="Nom de l'entreprise"
                                    />
                                    {errors.nom_entreprise && <p className="mt-1 text-sm text-red-600">{errors.nom_entreprise}</p>}
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Date de début <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        name="date_debut"
                                        value={formData.date_debut}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.date_debut ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                    />
                                    {errors.date_debut && <p className="mt-1 text-sm text-red-600">{errors.date_debut}</p>}
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Date de fin <span className="text-gray-500 text-xs">(laisser vide si poste actuel)</span>
                                    </label>
                                    <input
                                        type="date"
                                        name="date_fin"
                                        value={formData.date_fin}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.date_fin ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                    />
                                    {errors.date_fin && <p className="mt-1 text-sm text-red-600">{errors.date_fin}</p>}
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Pays <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="pays"
                                        value={formData.pays}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.pays ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                    >
                                        <option value="">Sélectionnez le pays</option>
                                        {countryOptions.map(country => (
                                            <option key={country.value} value={country.value}>
                                                {country.label}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.pays && <p className="mt-1 text-sm text-red-600">{errors.pays}</p>}
                                </div>
                                
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Description des tâches <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        rows={4}
                                        className={`w-full px-4 py-2 border ${errors.description ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                        placeholder="Décrivez vos responsabilités et réalisations"
                                    />
                                    {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
                                </div>
                            </div>
                            <div className="mt-6 flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={`px-4 py-2 rounded-lg text-white ${loading ? 'bg-orange-400' : 'bg-orange-600 hover:bg-orange-700'}`}
                                >
                                    {loading ? (
                                        <span className="flex items-center">
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Enregistrement...
                                        </span>
                                    ) : (
                                        isEditMode ? "Mettre à jour" : "Ajouter"
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal de confirmation de suppression */}
            {deleteConfirmation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
                        <div className="p-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Confirmer la suppression</h3>
                            <p className="text-gray-600 mb-6">
                                Êtes-vous sûr de vouloir supprimer cette expérience professionnelle ? Cette action est irréversible.
                            </p>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setDeleteConfirmation(null)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={() => handleDelete(deleteConfirmation)}
                                    className="px-4 py-2 rounded-lg text-white bg-red-600 hover:bg-red-700"
                                >
                                    Supprimer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExperiencePage;
